import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  EyeOff,
  Link2,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  AdminAssessment,
  AdminAssessmentSection,
  AdminAssessmentWorkshop,
  assessmentsService,
  coursesService,
} from '../services/api';
import { AssessmentAnalytics } from './assessments/AssessmentAnalytics';
import { AssessmentInvites } from './assessments/AssessmentInvites';
import { assessmentPath, publicLink, smsLink } from './assessments/links';
import { Course } from '../types';

type TabKey = 'structure' | 'workshops' | 'invites' | 'results';

const fa = (n: number) => n.toLocaleString('fa-IR');

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'structure', label: 'بخش‌ها و سوال‌ها' },
  { key: 'workshops', label: 'عناوین کارگاه' },
  { key: 'invites', label: 'دعوت‌نامه‌ها' },
  { key: 'results', label: 'گزارش تحلیلی' },
];

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500';

const Assessments: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('structure');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copiedLink, setCopiedLink] = useState<'full' | 'sms' | ''>('');

  /** نسخهٔ «پیامک» بدون //:https است چون هر ۷۰ کاراکتر فارسی یک بخش هزینه دارد */
  const copyLink = async (kind: 'full' | 'sms', slug: string) => {
    const full = publicLink(slug);
    const link = kind === 'sms' ? smsLink(full) : full;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(kind);
      setTimeout(() => setCopiedLink(''), 1800);
    } catch {
      window.prompt('لینک را کپی کنید:', link);
    }
  };
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [assessments, setAssessments] = useState<AdminAssessment[]>([]);
  const [activeId, setActiveId] = useState('');
  const [detail, setDetail] = useState<AdminAssessment | null>(null);
  const [workshops, setWorkshops] = useState<AdminAssessmentWorkshop[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [linkable, setLinkable] = useState<Array<{ id: string; title: string; date: string }>>([]);

  const [openSectionId, setOpenSectionId] = useState('');

  const flash = (message: string) => {
    setNotice(message);
    setTimeout(() => setNotice(''), 2600);
  };

  const handleError = (err: any, fallback: string) => {
    setError(err?.response?.data?.message || fallback);
    setTimeout(() => setError(''), 5000);
  };

  const loadDetail = useCallback(async (id: string) => {
    if (!id) {
      setDetail(null);
      return;
    }
    // شمارندهٔ پاسخ کارگاه‌ها به پرسشنامهٔ انتخاب‌شده وابسته است، پس با هم تازه می‌شوند
    const [assessment, workshopList] = await Promise.all([
      assessmentsService.getById(id),
      assessmentsService.listWorkshops(id).catch(() => null),
    ]);
    setDetail(assessment);
    if (workshopList) setWorkshops(workshopList);
  }, []);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    try {
      const [list, workshopList, courseList, linkableList] = await Promise.all([
        assessmentsService.list(),
        assessmentsService.listWorkshops(),
        coursesService.getAll(),
        assessmentsService.listLinkableWorkshops().catch(() => []),
      ]);
      setAssessments(list);
      setWorkshops(workshopList);
      setCourses(courseList);
      setLinkable(linkableList);

      const first = list[0]?.id || '';
      setActiveId(first);
      await loadDetail(first);
    } catch (err: any) {
      handleError(err, 'دریافت اطلاعات پرسشنامه انجام نشد.');
    } finally {
      setLoading(false);
    }
  }, [loadDetail]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // مرجع پایدار لازم است وگرنه هر رندر یک آرایهٔ تازه می‌سازد و useMemo پایین بی‌اثر می‌شود
  const sections = useMemo(() => detail?.sections ?? [], [detail]);

  const unlinkedCount = useMemo(
    () => sections.filter((s) => !s.courseId).length,
    [sections],
  );

  /** حالت تحلیل: به‌جای اتصال دوره، هر بخش سه متن تحلیل دارد و نتیجه با CTA تمام می‌شود */
  const isAnalysis = detail?.resultMode === 'ANALYSIS';

  // ── اکشن‌های پرسشنامه ──────────────────────────────────────────────────────

  const withBusy = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true);
    try {
      await fn();
    } catch (err: any) {
      handleError(err, fallback);
    } finally {
      setBusy(false);
    }
  };

  const createAssessment = () =>
    withBusy(async () => {
      const title = window.prompt('عنوان پرسشنامهٔ جدید:');
      if (!title?.trim()) return;
      const created = await assessmentsService.create({ title: title.trim() });
      setAssessments((prev) => [created, ...prev]);
      setActiveId(created.id);
      await loadDetail(created.id);
      flash('پرسشنامه ساخته شد.');
    }, 'ساخت پرسشنامه انجام نشد.');

  const patchAssessment = (data: Partial<AdminAssessment>) =>
    withBusy(async () => {
      if (!detail) return;
      const updated = await assessmentsService.update(detail.id, data);
      setDetail((prev) => (prev ? { ...prev, ...updated } : prev));
      setAssessments((prev) => prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)));
      flash('ذخیره شد.');
    }, 'ذخیره انجام نشد.');

  const addSection = () =>
    withBusy(async () => {
      if (!detail) return;
      const title = window.prompt('عنوان بخش جدید:');
      if (!title?.trim()) return;
      await assessmentsService.createSection(detail.id, { title: title.trim() });
      await loadDetail(detail.id);
      flash('بخش اضافه شد.');
    }, 'ساخت بخش انجام نشد.');

  const patchSection = (sectionId: string, data: Partial<AdminAssessmentSection>) =>
    withBusy(async () => {
      await assessmentsService.updateSection(sectionId, data);
      if (detail) await loadDetail(detail.id);
    }, 'ذخیرهٔ بخش انجام نشد.');

  const removeSection = (section: AdminAssessmentSection) =>
    withBusy(async () => {
      if (
        !window.confirm(
          `بخش «${section.title}» و ${fa(section.questions.length)} سوالش حذف شود؟ نتایج ثبت‌شدهٔ این بخش هم پاک می‌شود.`,
        )
      ) {
        return;
      }
      await assessmentsService.deleteSection(section.id);
      if (detail) await loadDetail(detail.id);
      flash('بخش حذف شد.');
    }, 'حذف بخش انجام نشد.');

  const addQuestion = (sectionId: string) =>
    withBusy(async () => {
      const text = window.prompt('متن سوال جدید:');
      if (!text?.trim()) return;
      await assessmentsService.createQuestion(sectionId, { text: text.trim() });
      if (detail) await loadDetail(detail.id);
    }, 'افزودن سوال انجام نشد.');

  const patchQuestion = (questionId: string, data: { text?: string; reverseScored?: boolean }) =>
    withBusy(async () => {
      await assessmentsService.updateQuestion(questionId, data);
      if (detail) await loadDetail(detail.id);
    }, 'ذخیرهٔ سوال انجام نشد.');

  const removeQuestion = (questionId: string) =>
    withBusy(async () => {
      if (!window.confirm('این سوال حذف شود؟')) return;
      await assessmentsService.deleteQuestion(questionId);
      if (detail) await loadDetail(detail.id);
    }, 'حذف سوال انجام نشد.');

  // ── اکشن‌های کارگاه ────────────────────────────────────────────────────────

  const addWorkshop = () =>
    withBusy(async () => {
      const title = window.prompt('عنوان کارگاه جدید:');
      if (!title?.trim()) return;
      const created = await assessmentsService.createWorkshop({
        title: title.trim(),
        sortOrder: workshops.length,
      });
      setWorkshops((prev) => [...prev, created]);
      flash('کارگاه اضافه شد.');
    }, 'ساخت کارگاه انجام نشد.');

  const patchWorkshop = (id: string, data: Partial<AdminAssessmentWorkshop>) =>
    withBusy(async () => {
      const updated = await assessmentsService.updateWorkshop(id, data);
      setWorkshops((prev) => prev.map((w) => (w.id === id ? { ...w, ...updated } : w)));
    }, 'ذخیرهٔ کارگاه انجام نشد.');

  const removeWorkshop = (workshop: AdminAssessmentWorkshop) =>
    withBusy(async () => {
      if (!window.confirm(`عنوان کارگاه «${workshop.title}» حذف شود؟`)) return;
      await assessmentsService.deleteWorkshop(workshop.id);
      setWorkshops((prev) => prev.filter((w) => w.id !== workshop.id));
      flash('کارگاه حذف شد.');
    }, 'حذف کارگاه انجام نشد.');

  if (loading) return <LoadingSpinner />;

  return (
    <div dir="rtl">
      <PageHeader
        title="پرسشنامه‌ها"
        description="مسیر رشد (اولویت‌بندی دوره‌ها) و پرسشنامه‌های تحلیلی با فراخوان به کارگاه — سوالات، متن‌ها، دعوت‌نامه‌ها و نتایج"
        action={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={bootstrap}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-bold text-gray-700"
            >
              <RefreshCw className="h-4 w-4" />
              تازه‌سازی
            </button>
            <button
              type="button"
              onClick={createAssessment}
              className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-bold text-white"
            >
              <Plus className="h-4 w-4" />
              پرسشنامهٔ جدید
            </button>
          </div>
        }
      />

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-bold text-green-700">
          {notice}
        </div>
      ) : null}

      {!assessments.length ? (
        <div className="rounded-xl border border-gray-200 bg-white p-10 text-center">
          <p className="text-sm font-bold text-gray-500">
            هنوز پرسشنامه‌ای ساخته نشده است. برای وارد کردن سوالات فایل ورد، دستور
            <code className="mx-1 rounded bg-gray-100 px-1.5 py-0.5">npm run seed:assessment</code>
            را در پوشهٔ backend اجرا کنید.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
            <label className="text-sm font-bold text-gray-600">پرسشنامه:</label>
            <select
              value={activeId}
              onChange={async (e) => {
                setActiveId(e.target.value);
                await loadDetail(e.target.value);
              }}
              className={`${inputClass} w-auto min-w-[240px]`}
            >
              {assessments.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.title} {a.published ? '' : '(پیش‌نویس)'}
                </option>
              ))}
            </select>

            {detail ? (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => patchAssessment({ published: !detail.published })}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-bold ${
                    detail.published
                      ? 'bg-green-600 text-white'
                      : 'border border-gray-300 bg-white text-gray-700'
                  }`}
                >
                  {detail.published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  {detail.published ? 'منتشر شده' : 'پیش‌نویس'}
                </button>
                {detail.published ? (
                  <span className="flex flex-wrap items-center gap-1.5 text-xs font-bold text-gray-500">
                    لینک عمومی:
                    <code className="rounded bg-gray-100 px-1.5 py-0.5">{publicLink(detail.slug)}</code>
                    <button
                      type="button"
                      onClick={() => copyLink('full', detail.slug)}
                      className="flex items-center gap-1 rounded-lg bg-blue-600 px-2.5 py-1 text-[11px] font-bold text-white"
                    >
                      {copiedLink === 'full' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      کپی
                    </button>
                    <button
                      type="button"
                      onClick={() => copyLink('sms', detail.slug)}
                      title={`${smsLink(publicLink(detail.slug))} — ${smsLink(publicLink(detail.slug)).length} کاراکتر`}
                      className="flex items-center gap-1 rounded-lg border border-blue-300 bg-white px-2.5 py-1 text-[11px] font-bold text-blue-700"
                    >
                      {copiedLink === 'sms' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      کپی برای پیامک
                    </button>
                  </span>
                ) : null}
              </>
            ) : null}
          </div>

          <div className="mb-4 flex gap-1 border-b border-gray-200">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 text-sm font-bold transition ${
                  tab === t.key
                    ? 'border-b-2 border-blue-600 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'structure' && detail ? (
            <div className="space-y-4">
              {/* key: فیلدها غیرکنترل‌شده‌اند (defaultValue)؛ با عوض شدن پرسشنامه در لیست
                  کشویی باید از نو ساخته شوند، وگرنه مقدار پرسشنامهٔ قبلی روی این یکی ذخیره می‌شد */}
              <div key={detail.id} className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-xs font-bold text-gray-600">عنوان</span>
                    <input
                      defaultValue={detail.title}
                      onBlur={(e) => {
                        const title = e.target.value.trim();
                        if (!title) {
                          e.target.value = detail.title;
                          return;
                        }
                        if (title !== detail.title) patchAssessment({ title });
                      }}
                      className={inputClass}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-bold text-gray-600">
                      توضیح کوتاه (در صفحهٔ لندینگ مهمان دیده می‌شود)
                    </span>
                    <input
                      defaultValue={detail.description || ''}
                      onBlur={(e) => patchAssessment({ description: e.target.value })}
                      className={inputClass}
                    />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="mb-1 block text-xs font-bold text-gray-600">متن مقدمه</span>
                    <textarea
                      defaultValue={detail.introText || ''}
                      onBlur={(e) => patchAssessment({ introText: e.target.value })}
                      rows={2}
                      className={inputClass}
                    />
                  </label>

                  <label className="block md:col-span-2">
                    <span className="mb-1 block text-xs font-bold text-gray-600">نوع نتیجه</span>
                    <select
                      value={detail.resultMode || 'COURSE_PRIORITY'}
                      disabled={busy}
                      onChange={(e) =>
                        patchAssessment({
                          resultMode: e.target.value as 'COURSE_PRIORITY' | 'ANALYSIS',
                        })
                      }
                      className={inputClass}
                    >
                      <option value="COURSE_PRIORITY">اولویت‌بندی دوره‌ها (مسیر رشد)</option>
                      <option value="ANALYSIS">تحلیل متنی هر محور + فراخوان به کارگاه (CTA)</option>
                    </select>
                    <span className="mt-1 block text-[11px] font-bold text-gray-400">
                      در حالت تحلیل، برای هر بخش سه متن (فعال / نیمه‌فعال / آرام) می‌نویسید و
                      صفحهٔ نتیجهٔ کاربر با دکمهٔ فراخوان زیر تمام می‌شود.
                    </span>
                  </label>

                  <label className="block md:col-span-2">
                    <span className="mb-1 block text-xs font-bold text-gray-600">نوع پاسخ</span>
                    <select
                      value={detail.scaleType || 'LIKERT_5'}
                      disabled={busy}
                      onChange={(e) =>
                        patchAssessment({ scaleType: e.target.value as 'LIKERT_5' | 'YES_NO' })
                      }
                      className={inputClass}
                    >
                      <option value="LIKERT_5">پنج گزینه‌ای (کاملاً موافقم … مخالفم)</option>
                      <option value="YES_NO">بله / خیر</option>
                    </select>
                    <span className="mt-1 block text-[11px] font-bold text-gray-400">
                      در حالت بله/خیر هر «بله» یعنی مسئله وجود دارد. شدت = درصد بله‌ها؛ ۷۰٪ به بالا
                      «فعال»، ۳۵٪ به بالا «نیمه‌فعال». (برای ۱۴ سوال: ۱۰ بله فعال، ۵ بله نیمه‌فعال)
                    </span>
                  </label>

                  {isAnalysis ? (
                    <div className="grid gap-3 rounded-lg border border-violet-200 bg-violet-50/40 p-3 md:col-span-2 md:grid-cols-3">
                      <label className="block">
                        <span className="mb-1 block text-xs font-bold text-gray-600">
                          عنوان فراخوان
                        </span>
                        <input
                          defaultValue={detail.ctaTitle || ''}
                          onBlur={(e) => patchAssessment({ ctaTitle: e.target.value })}
                          placeholder="کارگاه شفای زخم — استاد فراز قورچیان"
                          className={inputClass}
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs font-bold text-gray-600">
                          متن دکمه
                        </span>
                        <input
                          defaultValue={detail.ctaLabel || ''}
                          onBlur={(e) => patchAssessment({ ctaLabel: e.target.value })}
                          placeholder="ثبت‌نام در کارگاه شفای زخم"
                          className={inputClass}
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs font-bold text-gray-600">
                          آدرس دکمه (مسیر داخلی یا لینک کامل)
                        </span>
                        <input
                          defaultValue={detail.ctaUrl || ''}
                          onBlur={(e) => patchAssessment({ ctaUrl: e.target.value })}
                          placeholder="/kargah-shefaye-zakhm"
                          dir="ltr"
                          className={inputClass}
                        />
                      </label>
                      <label className="block md:col-span-3">
                        <span className="mb-1 block text-xs font-bold text-gray-600">
                          متن فراخوان (زیر عنوان، بالای دکمه)
                        </span>
                        <textarea
                          defaultValue={detail.ctaText || ''}
                          onBlur={(e) => patchAssessment({ ctaText: e.target.value })}
                          rows={3}
                          className={inputClass}
                        />
                      </label>
                      {!detail.ctaUrl ? (
                        <p className="text-[11px] font-bold text-amber-700 md:col-span-3">
                          تا وقتی آدرس دکمه خالی باشد، فراخوان در صفحهٔ نتیجه نمایش داده نمی‌شود.
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  {/* یک‌بارمصرف بودن هم روی سرور اعمال می‌شود و هم دکمهٔ
                      «پاسخ‌دهی مجدد» را از صفحهٔ نتیجهٔ کاربر برمی‌دارد */}
                  <label className="flex items-start gap-2 md:col-span-2">
                    <input
                      type="checkbox"
                      checked={!detail.allowRetake}
                      disabled={busy}
                      onChange={(e) => patchAssessment({ allowRetake: !e.target.checked })}
                      className="mt-1 h-4 w-4"
                    />
                    <span className="text-xs font-bold text-gray-700">
                      هر کاربر برای هر کارگاه فقط یک‌بار می‌تواند پاسخ دهد
                      <span className="mt-0.5 block text-[11px] font-bold text-gray-400">
                        با فعال بودن این گزینه، بعد از ثبت نهایی فقط نتیجه را می‌بیند و برای
                        همان کارگاه دوباره نمی‌تواند شرکت کند؛ اما شرکت‌کنندهٔ کارگاه جدید که
                        قبلاً برای کارگاه قبلی پاسخ داده، می‌تواند برای کارگاه جدید از نو
                        پاسخ دهد.
                      </span>
                    </span>
                  </label>

                  <label className="flex items-start gap-2 md:col-span-2">
                    <input
                      type="checkbox"
                      checked={detail.useWorkshops !== false}
                      disabled={busy}
                      onChange={(e) => patchAssessment({ useWorkshops: e.target.checked })}
                      className="mt-1 h-4 w-4"
                    />
                    <span className="text-xs font-bold text-gray-700">
                      پاسخ‌ها به نام «عنوان کارگاه» ثبت شوند
                      <span className="mt-0.5 block text-[11px] font-bold text-gray-400">
                        کاربر هنگام شروع، کارگاهش را انتخاب می‌کند. اگر فقط یک عنوان کارگاه
                        «فعال» باشد، همان از پیش انتخاب می‌شود و لینک عمومی هم به نام آن ثبت
                        می‌شود. برای پرسشنامهٔ مخاطب جدید (مثل تست سحر روانی) خاموش کنید.
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              {!isAnalysis && unlinkedCount > 0 ? (
                <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <p className="text-sm font-bold text-amber-800">
                    {fa(unlinkedCount)} بخش هنوز به دوره‌ای وصل نشده است. تا وقتی وصل نشوند، در
                    نتیجهٔ کاربر امتیاز نمایش داده می‌شود اما دوره‌ای پیشنهاد نمی‌شود.
                  </p>
                </div>
              ) : null}

              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-gray-600">
                  {fa(sections.length)} بخش ·{' '}
                  {fa(sections.reduce((sum, s) => sum + s.questions.length, 0))} سوال
                </p>
                <button
                  type="button"
                  onClick={addSection}
                  className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-bold text-white"
                >
                  <Plus className="h-4 w-4" />
                  بخش جدید
                </button>
              </div>

              {sections.map((section, index) => {
                const open = openSectionId === section.id;
                return (
                  <div key={section.id} className="rounded-xl border border-gray-200 bg-white">
                    <div className="flex flex-wrap items-center gap-2 p-3">
                      <button
                        type="button"
                        onClick={() => setOpenSectionId(open ? '' : section.id)}
                        className="flex items-center gap-2 text-sm font-bold text-gray-800"
                      >
                        {open ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                        <span className="text-gray-400">{fa(index + 1)}.</span>
                        {section.title}
                      </button>

                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-bold text-gray-600">
                        {fa(section.questions.length)} سوال
                      </span>

                      {isAnalysis && section.scored !== false ? (
                        section.analysisHigh && section.analysisMedium && section.analysisLow ? (
                          <span className="rounded-full bg-violet-50 px-2 py-0.5 text-xs font-bold text-violet-700">
                            تحلیل کامل
                          </span>
                        ) : (
                          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-700">
                            متن تحلیل ناقص
                          </span>
                        )
                      ) : isAnalysis ? null : section.courseId ? (
                        <span className="flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-bold text-blue-700">
                          <Link2 className="h-3 w-3" />
                          {section.course?.title || 'دورهٔ متصل'}
                        </span>
                      ) : (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-700">
                          بدون دوره
                        </span>
                      )}

                      {section.workshopId ? (
                        <span className="rounded-full bg-purple-50 px-2 py-0.5 text-xs font-bold text-purple-700">
                          ویژهٔ {section.workshop?.title}
                        </span>
                      ) : null}

                      {section.scored === false ? (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-bold text-gray-600">
                          بدون امتیاز
                        </span>
                      ) : null}

                      <div className="mr-auto flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => patchSection(section.id, { published: !section.published })}
                          className={`rounded-lg px-2.5 py-1.5 text-xs font-bold ${
                            section.published
                              ? 'bg-green-50 text-green-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {section.published ? 'فعال' : 'غیرفعال'}
                        </button>
                        <button
                          type="button"
                          title="بخش بدون امتیاز پرسیده و ذخیره می‌شود اما در نتیجهٔ کاربر نمی‌آید (مثل سوال‌های آمادگی برای تغییر)"
                          onClick={() =>
                            patchSection(section.id, { scored: section.scored === false })
                          }
                          className={`rounded-lg px-2.5 py-1.5 text-xs font-bold ${
                            section.scored === false
                              ? 'bg-gray-100 text-gray-500'
                              : 'bg-blue-50 text-blue-700'
                          }`}
                        >
                          {section.scored === false ? 'بدون امتیاز' : 'امتیازدار'}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeSection(section)}
                          className="rounded-lg p-1.5 text-red-600 hover:bg-red-50"
                          aria-label="حذف بخش"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {open ? (
                      <div className="border-t border-gray-100 p-4">
                        <div className="grid gap-3 md:grid-cols-3">
                          <label className="block">
                            <span className="mb-1 block text-xs font-bold text-gray-600">
                              عنوان بخش
                            </span>
                            <input
                              defaultValue={section.title}
                              onBlur={(e) =>
                                e.target.value.trim() !== section.title &&
                                patchSection(section.id, { title: e.target.value.trim() })
                              }
                              className={inputClass}
                            />
                          </label>

                          {!isAnalysis ? (
                            <label className="block">
                              <span className="mb-1 block text-xs font-bold text-gray-600">
                                دورهٔ پیشنهادی
                              </span>
                              <select
                                value={section.courseId || ''}
                                onChange={(e) =>
                                  patchSection(section.id, { courseId: e.target.value })
                                }
                                className={inputClass}
                              >
                                <option value="">— بدون دوره —</option>
                                {courses.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.title}
                                  </option>
                                ))}
                              </select>
                            </label>
                          ) : null}

                          <label className="block">
                            <span className="mb-1 block text-xs font-bold text-gray-600">
                              ویژهٔ کارگاه
                            </span>
                            <select
                              value={section.workshopId || ''}
                              onChange={(e) =>
                                patchSection(section.id, { workshopId: e.target.value })
                              }
                              className={inputClass}
                            >
                              <option value="">— برای همه —</option>
                              {workshops.map((w) => (
                                <option key={w.id} value={w.id}>
                                  {w.title}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>

                        {isAnalysis && section.scored !== false ? (
                          <div className="mt-4 rounded-lg border border-violet-200 bg-violet-50/40 p-3">
                            <p className="mb-2 text-xs font-bold text-violet-800">
                              متن تحلیل این محور — بر اساس شدت پاسخ‌های کاربر یکی از این سه متن
                              در صفحهٔ نتیجه نمایش داده می‌شود.
                            </p>
                            <div className="grid gap-3 md:grid-cols-3">
                              {(
                                [
                                  { key: 'analysisHigh', label: 'فعال', hint: 'شدت ۷۰٪ به بالا' },
                                  { key: 'analysisMedium', label: 'نیمه‌فعال', hint: 'شدت ۳۵ تا ۶۹٪' },
                                  { key: 'analysisLow', label: 'آرام', hint: 'شدت کمتر از ۳۵٪' },
                                ] as const
                              ).map((band) => (
                                <label key={band.key} className="block">
                                  <span className="mb-1 block text-xs font-bold text-gray-600">
                                    {band.label}
                                    <span className="mr-1 text-[10px] font-bold text-gray-400">
                                      ({band.hint})
                                    </span>
                                  </span>
                                  <textarea
                                    defaultValue={section[band.key] || ''}
                                    rows={7}
                                    onBlur={(e) =>
                                      (e.target.value.trim() || '') !== (section[band.key] || '') &&
                                      patchSection(section.id, { [band.key]: e.target.value.trim() })
                                    }
                                    className={inputClass}
                                  />
                                </label>
                              ))}
                            </div>
                          </div>
                        ) : null}

                        <div className="mt-4 flex items-center justify-between">
                          <p className="text-xs font-bold text-gray-500">
                            «معکوس» را برای سوالی بزنید که با جملهٔ مثبت نوشته شده — امتیازش
                            برعکس حساب می‌شود.
                          </p>
                          <button
                            type="button"
                            onClick={() => addQuestion(section.id)}
                            className="flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-bold text-blue-700"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            سوال جدید
                          </button>
                        </div>

                        <div className="mt-2 space-y-2">
                          {section.questions.map((question, qIndex) => (
                            <div
                              key={question.id}
                              className="flex items-start gap-2 rounded-lg border border-gray-200 p-2"
                            >
                              <span className="mt-2 w-6 shrink-0 text-center text-xs font-bold text-gray-400">
                                {fa(qIndex + 1)}
                              </span>
                              <textarea
                                defaultValue={question.text}
                                rows={2}
                                onBlur={(e) =>
                                  e.target.value.trim() !== question.text &&
                                  patchQuestion(question.id, { text: e.target.value.trim() })
                                }
                                className={`${inputClass} flex-1`}
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  patchQuestion(question.id, {
                                    reverseScored: !question.reverseScored,
                                  })
                                }
                                className={`mt-1 shrink-0 rounded-lg px-2.5 py-1.5 text-xs font-bold ${
                                  question.reverseScored
                                    ? 'bg-purple-100 text-purple-700'
                                    : 'bg-gray-100 text-gray-500'
                                }`}
                              >
                                {question.reverseScored ? 'معکوس' : 'عادی'}
                              </button>
                              <button
                                type="button"
                                onClick={() => removeQuestion(question.id)}
                                className="mt-1 shrink-0 rounded-lg p-1.5 text-red-600 hover:bg-red-50"
                                aria-label="حذف سوال"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null}

          {tab === 'workshops' ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-gray-600">
                  عناوینی که مشتری هنگام شروع پرسشنامه بین آنها انتخاب می‌کند. وقتی فقط یک عنوان
                  «فعال» باشد، پاسخ‌های لینک عمومی خودکار به نام همان ثبت می‌شوند؛ کارگاه‌های
                  تمام‌شده را غیرفعال کنید.
                </p>
                <button
                  type="button"
                  onClick={addWorkshop}
                  className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-bold text-white"
                >
                  <Plus className="h-4 w-4" />
                  عنوان جدید
                </button>
              </div>

              {workshops.map((w) => (
                <div key={w.id} className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="grid gap-3 md:grid-cols-3">
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold text-gray-600">عنوان</span>
                      <input
                        defaultValue={w.title}
                        onBlur={(e) =>
                          e.target.value.trim() !== w.title &&
                          patchWorkshop(w.id, { title: e.target.value.trim() })
                        }
                        className={inputClass}
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold text-gray-600">توضیح</span>
                      <input
                        defaultValue={w.description || ''}
                        onBlur={(e) => patchWorkshop(w.id, { description: e.target.value })}
                        className={inputClass}
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold text-gray-600">
                        اتصال به کارگاه واقعی (تشخیص خودکار سابقهٔ شرکت)
                      </span>
                      <select
                        value={w.linkedWorkshopId || ''}
                        onChange={(e) => patchWorkshop(w.id, { linkedWorkshopId: e.target.value })}
                        className={inputClass}
                      >
                        <option value="">— بدون اتصال —</option>
                        {linkable.map((lw) => (
                          <option key={lw.id} value={lw.id}>
                            {lw.title} — {lw.date}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-bold text-gray-600">
                      لینک این کارگاه:{' '}
                      <code dir="ltr">{detail ? assessmentPath(detail.slug, { w: w.slug }) : '—'}</code>
                    </span>
                    <span
                      className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-bold text-gray-600"
                      title="پاسخ‌های تکمیل‌شدهٔ پرسشنامهٔ انتخاب‌شده که به نام این کارگاه ثبت شده‌اند"
                    >
                      {fa(w._count?.sections ?? 0)} بخش ویژه · {fa(w._count?.attempts ?? 0)} پاسخ
                      تکمیل‌شده
                    </span>
                    <div className="mr-auto flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => patchWorkshop(w.id, { isActive: !w.isActive })}
                        className={`rounded-lg px-2.5 py-1.5 text-xs font-bold ${
                          w.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {w.isActive ? 'فعال' : 'غیرفعال'}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeWorkshop(w)}
                        className="rounded-lg p-1.5 text-red-600 hover:bg-red-50"
                        aria-label="حذف کارگاه"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {tab === 'invites' && detail ? (
            <AssessmentInvites
              assessmentId={detail.id}
              assessmentSlug={detail.slug}
              inviteOnly={detail.accessMode === 'INVITE_ONLY'}
              workshops={workshops}
              onToggleAccessMode={(mode) => patchAssessment({ accessMode: mode })}
            />
          ) : null}

          {tab === 'results' && detail ? (
            <AssessmentAnalytics assessmentId={detail.id} workshops={workshops} />
          ) : null}
        </>
      )}
    </div>
  );
};

export default Assessments;
